// ─────────────────────────────────────────────────────────────
//  data.ts :  IntelQI Product Updates
// ─────────────────────────────────────────────────────────────

export type Product =
  'All' | 'UDC' | 'HRMS' | 'Payroll' | 'Accounts' | 'LMS' | 'SchoolERP'
export type UpdateType =
  'All' | 'New Feature' | 'Improvement' | 'Bug Fix' | 'Security'

/* ── VERSION ─────────────────────────────────────────────────
   One entry per release.
─────────────────────────────────────────────────────────────── */
export interface Version {
  version: string
  tagline: string
  releaseDate: string
  highlight: string
  products: Exclude<Product, 'All'>[]
}

export const VERSIONS: Version[] = [
  {
    version: 'v1.3.0',
    tagline: 'Lightning-fast everywhere',
    releaseDate: 'March 2026',
    highlight:
      'UDC gets the redesigned workspace with sidebar navigation and scrollable list views, HRMS introduces automatic overtime calculation, Accounts adds custom financial report templates, Payroll benefits from major background processing improvements, and SchoolERP launches GPS-integrated transport tracking with route-wise fee collection.',
    products: ['UDC', 'HRMS', 'Payroll', 'Accounts', 'SchoolERP'],
  },
  {
    version: 'v1.2.0',
    tagline: 'Low-code at your fingertips',
    releaseDate: 'February 2026',
    highlight:
      'UDC now includes a powerful No-code Form Builder and Visual Workflow Builder, HRMS brings enhanced leave and attendance controls, Accounts allows post-submission editable invoices, LMS features improved progress tracking, and SchoolERP introduces an online Admission Management module with a parent-facing application portal.',
    products: ['UDC', 'HRMS', 'Accounts', 'LMS', 'SchoolERP'],
  },
  {
    version: 'v1.1.0',
    tagline: 'A refreshed foundation',
    releaseDate: 'January 2026',
    highlight:
      'New modern design system rolled out across all modules, significantly faster payroll engine, new Performance Management features in HRMS, stock reservation in Accounts, hundreds of stability improvements, and SchoolERP adds Fee & Finance Management with online payment gateway integration and automated GST receipts.',
    /* LMS deliberately absent: this release ships no LMS update, and the
       index filter + version page derive their sections from UPDATES, so a
       product listed here without a matching update is a broken promise —
       the header advertises a section that never renders. Keep this list in
       lockstep with the UPDATES entries for the version. */
    products: ['UDC', 'HRMS', 'Payroll', 'Accounts', 'SchoolERP'],
  },
  {
    version: 'v1.0.0',
    tagline: 'Initial Launch',
    releaseDate: 'December 2025',
    highlight:
      'Official launch of IntelQI with core performance optimisations and essential HR, Payroll, and Accounting capabilities. SchoolERP launches with its foundational Student Information System, Academic Planning, and HRMS modules: covering student profiles, timetable management, and staff records.',
    // Payroll deliberately absent — no v1.0.0 payroll update exists (see note
    // on v1.1.0 above).
    products: ['UDC', 'HRMS', 'SchoolERP'],
  },
]

/* ── UPDATE ──────────────────────────────────────────────────
   `version` links to Version.version.
   `sectionOrder` controls display order within a product group.
─────────────────────────────────────────────────────────────── */
export interface Update {
  id: string
  date: string
  month: string
  product: Exclude<Product, 'All'>
  type: Exclude<UpdateType, 'All'>
  title: string
  description: string
  bullets: string[]
  version: string
  major?: boolean
  sectionOrder?: number
}

export const UPDATES: Update[] = [
  /* ── v1.3.0 ──────────────────────────────────────────────── */
  {
    id: 'v130-udc-1',
    date: 'Mar 28, 2026',
    month: 'March 2026',
    product: 'UDC',
    type: 'New Feature',
    version: 'v1.3.0',
    major: true,
    sectionOrder: 1,
    title: 'Redesigned Workspace with Sidebar',
    description:
      'Workspaces now feature a clean sidebar navigation where related modules are grouped neatly. Icons are searchable, reorderable, and easier to access.',
    bullets: [
      'Persistent sidebar with collapsible sections',
      'Searchable and reorderable module icons',
      'Related items grouped logically',
      'Modern card-based layout with smooth transitions',
    ],
  },
  {
    id: 'v130-udc-2',
    date: 'Mar 25, 2026',
    month: 'March 2026',
    product: 'UDC',
    type: 'New Feature',
    version: 'v1.3.0',
    sectionOrder: 2,
    title: 'Scrollable List Views with Unlimited Columns',
    description:
      'List views now support horizontal scrolling, sticky columns, and unlimited visible fields with improved rendering performance.',
    bullets: [
      'Horizontal scrolling with sticky first column',
      'Resizable columns saved per user',
      'Unlimited columns support',
      'Faster rendering on large datasets',
    ],
  },

  {
    id: 'v130-hrms-1',
    date: 'Mar 22, 2026',
    month: 'March 2026',
    product: 'HRMS',
    type: 'New Feature',
    version: 'v1.3.0',
    major: true,
    sectionOrder: 1,
    title: 'Automatic Overtime Management',
    description:
      'Overtime is now automatically calculated from employee check-in/check-out times based on shift rules. Approved Overtime Slips directly generate Additional Salary entries for payroll.',
    bullets: [
      'Auto-calculation using shift-specific overtime types',
      'Overtime Slip approval workflow',
      'Direct integration with Payroll',
      'Support for multiple overtime rates and multipliers',
    ],
  },

  {
    id: 'v130-payroll-1',
    date: 'Mar 20, 2026',
    month: 'March 2026',
    product: 'Payroll',
    type: 'Improvement',
    version: 'v1.3.0',
    sectionOrder: 1,
    title: 'Background Heavy Payroll Processing',
    description:
      'Large payroll runs now process reliably in the background with real-time progress tracking and completion notifications.',
    bullets: [
      'No more UI timeouts for 1000+ employees',
      'Live progress indicator with estimated time',
      'Email notification on completion',
    ],
  },

  {
    id: 'v130-accounts-1',
    date: 'Mar 18, 2026',
    month: 'March 2026',
    product: 'Accounts',
    type: 'New Feature',
    version: 'v1.3.0',
    major: true,
    sectionOrder: 1,
    title: 'Custom Financial Report Templates',
    description:
      'Build fully custom P&L, Balance Sheet, and Cash Flow reports using reusable formula-driven templates with multi-currency and multi-company support.',
    bullets: [
      'Visual formula builder',
      'Reusable templates across companies',
      'Scheduled report generation & email',
      'Multi-currency consolidated views',
    ],
  },

  // ── SchoolERP v1.3.0 ────────────────────────────────────────
  {
    id: 'v130-schoolerp-1',
    date: 'Mar 15, 2026',
    month: 'March 2026',
    product: 'SchoolERP',
    type: 'New Feature',
    version: 'v1.3.0',
    major: true,
    sectionOrder: 1,
    title: 'GPS-Integrated Transport Management',
    description:
      'Live GPS tracking for school buses is now available directly in the admin dashboard and parent portal. Routes, stops, driver records, and transport fee collection are managed from a single transport hub.',
    bullets: [
      'Live bus location visible to admin and parents in real time',
      'Automated arrival alerts sent to parents via app and SMS',
      'Route-wise transport fee collection integrated with the fee module',
      'Driver licence, vehicle RC, insurance, and PUC expiry alerts',
      'Mid-year route adjustments with instant re-assignment of students',
    ],
  },
  {
    id: 'v130-schoolerp-2',
    date: 'Mar 12, 2026',
    month: 'March 2026',
    product: 'SchoolERP',
    type: 'New Feature',
    version: 'v1.3.0',
    sectionOrder: 2,
    title: 'Sports & Co-Curricular Management',
    description:
      'Schools can now manage sports teams, club rosters, match fixtures, and co-curricular participation records centrally. Every activity and achievement links directly to the student profile.',
    bullets: [
      'Create and manage sports teams with coach assignments and squad rosters',
      'Schedule inter-house and inter-school fixtures with automated notifications',
      'Log match results, individual performance stats, and fitness assessments',
      'Club management for music, drama, robotics, debate, and more',
      'Co-curricular portfolio built automatically from logged participation',
    ],
  },
  {
    id: 'v130-schoolerp-3',
    date: 'Mar 10, 2026',
    month: 'March 2026',
    product: 'SchoolERP',
    type: 'Improvement',
    version: 'v1.3.0',
    sectionOrder: 3,
    title: 'Bulk Report Card Generation',
    description:
      'Report cards can now be generated in bulk for an entire class or grade in a single action. Print-ready PDFs or direct parent email delivery: no manual processing.',
    bullets: [
      'Batch generation for full class or grade in one click',
      'Board-formatted PDF templates with configurable layouts',
      'Direct email delivery to parent accounts',
      'Marks and grades pulled automatically from the examination module',
    ],
  },

  /* ── v1.2.0 ──────────────────────────────────────────────── */
  {
    id: 'v120-udc-1',
    date: 'Feb 26, 2026',
    month: 'February 2026',
    product: 'UDC',
    type: 'New Feature',
    version: 'v1.2.0',
    major: true,
    sectionOrder: 1,
    title: 'No-Code Form Builder',
    description:
      'Create and modify forms directly in the UI with a visual drag-and-drop editor. Preview forms in real time without writing code.',
    bullets: [
      'Visual field layout editor',
      'Real-time form preview',
      'Full support for all field types and properties',
      'Version history for forms',
    ],
  },
  {
    id: 'v120-udc-2',
    date: 'Feb 24, 2026',
    month: 'February 2026',
    product: 'UDC',
    type: 'New Feature',
    version: 'v1.2.0',
    sectionOrder: 2,
    title: 'Visual Workflow Builder',
    description:
      'Design approval and business workflows using an intuitive drag-and-drop flowchart interface with condition-based branching.',
    bullets: [
      'Flowchart-style visual designer',
      'Condition-based branching and rules',
      'Automatic transition handling',
      'Full audit trail for changes',
    ],
  },

  {
    id: 'v120-accounts-1',
    date: 'Feb 18, 2026',
    month: 'February 2026',
    product: 'Accounts',
    type: 'New Feature',
    version: 'v1.2.0',
    major: true,
    sectionOrder: 1,
    title: 'Post-Submission Editable Invoices',
    description:
      'Edit account heads and cost centres in submitted Sales and Purchase Invoices without cancelling and amending, with full audit trail and automatic re-posting.',
    bullets: [
      'Safe editing of key fields post-submission',
      'Automatic re-posting of entries',
      'Complete audit trail maintained',
    ],
  },

  {
    id: 'v120-hrms-1',
    date: 'Feb 20, 2026',
    month: 'February 2026',
    product: 'HRMS',
    type: 'Improvement',
    version: 'v1.2.0',
    sectionOrder: 1,
    title: 'Enhanced Leave & Attendance Controls',
    description:
      'Improved control panel for leave policies, carry-forward rules, and attendance with better validation and night-shift handling.',
    bullets: [
      'Visual leave balance simulator',
      'Bulk policy updates across departments',
      'Improved night-shift attendance logic',
    ],
  },

  {
    id: 'v120-lms-1',
    date: 'Feb 15, 2026',
    month: 'February 2026',
    product: 'LMS',
    type: 'Improvement',
    version: 'v1.2.0',
    sectionOrder: 1,
    title: 'Improved Course Progress Tracking',
    description:
      'Progress is now saved frequently with seamless cross-device resume and enhanced admin analytics for completion rates.',
    bullets: [
      'Auto-save every few seconds',
      'Resume from exact point across devices',
      'Better completion rate dashboard',
    ],
  },

  // ── SchoolERP v1.2.0 ────────────────────────────────────────
  {
    id: 'v120-schoolerp-1',
    date: 'Feb 22, 2026',
    month: 'February 2026',
    product: 'SchoolERP',
    type: 'New Feature',
    version: 'v1.2.0',
    major: true,
    sectionOrder: 1,
    title: 'Online Admission Management',
    description:
      'Schools can now publish an online admission form directly from SchoolERP. Applications flow into a visual pipeline where staff move candidates through stages, schedule interviews, and convert accepted applicants to student records in one click: no re-entry.',
    bullets: [
      'Customisable online application form embeddable on the school website',
      'Kanban-style admission pipeline with stage-wise drag-and-drop',
      'Document upload and automatic completeness check',
      'Entrance test and interview scheduling with automated parent notifications',
      'One-click conversion of accepted applicant to full student profile',
      'Seat allocation and automatic waitlist management',
    ],
  },
  {
    id: 'v120-schoolerp-2',
    date: 'Feb 19, 2026',
    month: 'February 2026',
    product: 'SchoolERP',
    type: 'New Feature',
    version: 'v1.2.0',
    sectionOrder: 2,
    title: 'Parent Portal & PTM Scheduling',
    description:
      'Parents now have their own login portal to track attendance, view fee dues, access results, and book parent-teacher meeting slots: all without calling the school.',
    bullets: [
      'Personalised dashboard with attendance, dues, results, and notices',
      'Online PTM slot booking with post-meeting feedback by teachers',
      'Direct messaging with class teachers without sharing personal numbers',
      'Multi-child support from a single parent login',
      'Instant SMS and app alerts for absences, fee dues, and exam results',
    ],
  },
  {
    id: 'v120-schoolerp-3',
    date: 'Feb 14, 2026',
    month: 'February 2026',
    product: 'SchoolERP',
    type: 'Improvement',
    version: 'v1.2.0',
    sectionOrder: 3,
    title: 'Examination Module Enhancements',
    description:
      'The examination module now supports online MCQ tests with auto-grading, a reusable question bank, and instant result publishing. Offline exams support bulk mark import via Excel.',
    bullets: [
      'MCQ exam builder with randomised question sets',
      'Instant auto-grading on submission with results visible to students',
      'Reusable question bank across batches and academic years',
      'Bulk mark import from Excel template for offline exams',
      'Class-level and student-level performance analytics after result publication',
    ],
  },

  /* ── v1.1.0 ──────────────────────────────────────────────── */
  {
    id: 'v110-udc-1',
    date: 'Jan 28, 2026',
    month: 'January 2026',
    product: 'UDC',
    type: 'New Feature',
    version: 'v1.1.0',
    major: true,
    sectionOrder: 1,
    title: 'Modern Design System Rollout',
    description:
      'Platform-wide adoption of the new modern design system: consistent typography, colours, spacing, and interactions across all modules.',
    bullets: [
      'Unified clean and modern UI',
      'WCAG AA compliant contrast ratios',
      'Dark mode support everywhere',
      'Improved initial load performance',
    ],
  },

  {
    id: 'v110-hrms-1',
    date: 'Jan 25, 2026',
    month: 'January 2026',
    product: 'HRMS',
    type: 'New Feature',
    version: 'v1.1.0',
    major: true,
    sectionOrder: 1,
    title: 'Performance Management Module',
    description:
      'New built-in module for setting goals, conducting appraisals, collecting 360° feedback, and linking performance to salary increments.',
    bullets: [
      'Goal setting and tracking',
      'Self & manager appraisals',
      '360° feedback collection',
      'Performance-linked salary increments',
    ],
  },
  {
    /* Filed under Payroll, not HRMS: this is the payroll-engine change the
       v1.1.0 highlight advertises. Under HRMS the Payroll filter could never
       surface it, and v1.1.0's Payroll listing pointed at nothing. The id
       moves with it — it only feeds the #update-* anchor. */
    id: 'v110-payroll-1',
    date: 'Jan 22, 2026',
    month: 'January 2026',
    product: 'Payroll',
    type: 'Improvement',
    version: 'v1.1.0',
    sectionOrder: 2,
    title: '5× Faster Payroll Engine',
    description:
      'Completely optimised payroll processing: now significantly faster even for large employee counts with batch processing.',
    bullets: [
      'Batch processing with progress tracking',
      'Reduced memory usage',
      'Parallel salary slip generation',
    ],
  },

  {
    id: 'v110-accounts-1',
    date: 'Jan 20, 2026',
    month: 'January 2026',
    product: 'Accounts',
    type: 'New Feature',
    version: 'v1.1.0',
    sectionOrder: 1,
    title: 'Stock Reservation',
    description:
      'Reserve stock against sales orders effortlessly to prevent shortages and improve inventory accuracy.',
    bullets: [
      'One-click stock reservation from Sales Order',
      'Better coordination between sales and inventory',
      'Improved COGS accuracy',
    ],
  },

  // ── SchoolERP v1.1.0 ────────────────────────────────────────
  {
    id: 'v110-schoolerp-1',
    date: 'Jan 18, 2026',
    month: 'January 2026',
    product: 'SchoolERP',
    type: 'New Feature',
    version: 'v1.1.0',
    major: true,
    sectionOrder: 1,
    title: 'Fee & Finance Management with Online Payments',
    description:
      'Schools can now configure class-wise and category-wise fee structures and accept payments via UPI, NEFT, and card. Every payment auto-generates a GST-compliant receipt and reconciles instantly against the student ledger.',
    bullets: [
      'Class-wise and category-wise fee structure configuration',
      'Online payment gateway: UPI, NEFT, IMPS, debit/credit card',
      'GST-compliant digital receipts emailed to parents instantly',
      'Automated SMS and app reminders for upcoming and overdue dues',
      'Discount, scholarship, and late-fine rules applied automatically',
      'Real-time collection rate dashboard for school management',
    ],
  },
  {
    id: 'v110-schoolerp-2',
    date: 'Jan 15, 2026',
    month: 'January 2026',
    product: 'SchoolERP',
    type: 'New Feature',
    version: 'v1.1.0',
    sectionOrder: 2,
    title: 'Scholarship Management Module',
    description:
      'A dedicated digital workflow for scholarship applications, eligibility checking, multi-level approvals, and disbursement tracking. Approved amounts post directly to the student fee account.',
    bullets: [
      'Online scholarship application forms with document upload',
      'Automatic eligibility check against defined criteria (marks, income, category)',
      'Multi-level approval chain: class teacher, coordinator, principal',
      'Approved disbursement auto-adjusted in student fee dues',
      'Government-format scholarship reports for NSP and state portals',
    ],
  },
  {
    id: 'v110-schoolerp-3',
    date: 'Jan 12, 2026',
    month: 'January 2026',
    product: 'SchoolERP',
    type: 'Improvement',
    version: 'v1.1.0',
    sectionOrder: 3,
    title: 'Staff Payroll Integration for School HRMS',
    description:
      'Teaching and non-teaching staff payroll now runs directly inside SchoolERP. Attendance and leave data from the school HRMS feeds into salary calculations automatically: no re-entry, no Excel exports.',
    bullets: [
      'Salary structures for teaching and non-teaching staff grades',
      'LOP deductions and approved leave encashments pulled from attendance automatically',
      'PF, ESI, PT, and TDS computed per employee based on school-specific rules',
      'Password-protected payslips delivered to staff portal and email',
      'Payroll summary and department-wise cost reports generated post-run',
    ],
  },

  /* ── v1.0.0 ──────────────────────────────────────────────── */
  {
    id: 'v100-udc-1',
    date: 'Dec 28, 2025',
    month: 'December 2025',
    product: 'UDC',
    type: 'Improvement',
    version: 'v1.0.0',
    major: true,
    sectionOrder: 1,
    title: 'Platform Foundation & Performance Base',
    description:
      'Initial launch with core performance optimisations and a stable, high-performance foundation.',
    bullets: [
      'Improved page load times',
      'Revamped background job queue',
      'Solid infrastructure for future growth',
    ],
  },
  {
    id: 'v100-hrms-1',
    date: 'Dec 20, 2025',
    month: 'December 2025',
    product: 'HRMS',
    type: 'Improvement',
    version: 'v1.0.0',
    sectionOrder: 1,
    title: 'Bulk Leave Approval',
    description:
      'Managers can now approve or reject multiple leave requests in one action from the calendar or list view.',
    bullets: [
      'Multi-select support',
      'Single digest notification to employees',
      'Optional bulk comment field',
    ],
  },

  // ── SchoolERP v1.0.0 ────────────────────────────────────────
  {
    id: 'v100-schoolerp-1',
    date: 'Dec 15, 2025',
    month: 'December 2025',
    product: 'SchoolERP',
    type: 'New Feature',
    version: 'v1.0.0',
    major: true,
    sectionOrder: 1,
    title: 'Student Information System: Launch',
    description:
      'SchoolERP launches with a fully digital Student Information System. Every student gets a single profile covering personal, academic, and medical details: built from admission data with no re-entry required.',
    bullets: [
      'Comprehensive student profile: personal, academic, medical, and guardian details',
      'Class and section allocation with bulk year-end promotion',
      'Daily attendance marking with instant parent notification on absence',
      'Behaviour and discipline records linked to student profiles',
      'Instant student lookup by name, roll number, or guardian phone',
      'Promotion, lateral transfer, and TC issuance all from the same profile',
    ],
  },
  {
    id: 'v100-schoolerp-2',
    date: 'Dec 12, 2025',
    month: 'December 2025',
    product: 'SchoolERP',
    type: 'New Feature',
    version: 'v1.0.0',
    sectionOrder: 2,
    title: 'Academic Planning & Timetable Management',
    description:
      'Schools can now build and publish their full academic calendar, generate conflict-free timetables, and handle substitutions: all in one place. Staff, students, and parents see the same up-to-date schedule.',
    bullets: [
      'Full academic calendar: holidays, exam windows, events, and PTMs',
      'Teacher and student timetable builder with automatic clash detection',
      'Period scheduling with instant substitution assignment and staff notification',
      'Event planning with venue booking and participant management',
      'Automated reminders to teachers, parents, and students for key dates',
    ],
  },
  {
    id: 'v100-schoolerp-3',
    date: 'Dec 10, 2025',
    month: 'December 2025',
    product: 'SchoolERP',
    type: 'New Feature',
    version: 'v1.0.0',
    sectionOrder: 3,
    title: 'School Staff HRMS: Foundation',
    description:
      'Complete digital HR records for teaching and non-teaching staff. Employee profiles, leave management, recruitment, and document storage are available from day one: with payroll integration coming in v1.1.0.',
    bullets: [
      'Full employee profiles: designation, department, qualifications, and emergency contacts',
      'Digital leave applications with one-click manager approval',
      'Recruitment pipeline: job postings, applicant tracking, and interview scheduling',
      'Staff document storage: contracts, certificates, and appraisal records',
      'Exit management: resignation, dues calculation, and experience letter generation',
    ],
  },
]

/* ── HELPERS ─────────────────────────────────────────────────── */

export function getUpdatesByVersion(version: string): Update[] {
  return UPDATES.filter((u) => u.version === version)
}

export function groupByProduct(
  updates: Update[]
): Partial<Record<Exclude<Product, 'All'>, Update[]>> {
  const groups: Partial<Record<Exclude<Product, 'All'>, Update[]>> = {}
  updates.forEach((u) => {
    if (!groups[u.product]) groups[u.product] = []
    groups[u.product]!.push(u)
  })
  Object.values(groups).forEach((arr) =>
    arr!.sort((a, b) => (a.sectionOrder ?? 99) - (b.sectionOrder ?? 99))
  )
  return groups
}

export function groupByMonth(updates: Update[]): Record<string, Update[]> {
  const groups: Record<string, Update[]> = {}
  updates.forEach((u) => {
    ;(groups[u.month] ??= []).push(u)
  })
  return groups
}

export function countByType(
  version: string
): Record<Exclude<UpdateType, 'All'>, number> {
  const updates = getUpdatesByVersion(version)
  const counts = {
    'New Feature': 0,
    Improvement: 0,
    'Bug Fix': 0,
    Security: 0,
  } as Record<Exclude<UpdateType, 'All'>, number>
  updates.forEach((u) => counts[u.type]++)
  return counts
}

export function getVersion(slug: string): Version | undefined {
  return VERSIONS.find((v) => v.version === slug)
}
